import { FieldState, FormState } from 'formstate';
import { useWrapRequest } from 'use-wrap-request';

import {
    ActivityEventType,
    DailyStepsEvent,
    ActivityPriority,
    SupportedFitnessApp
} from '@api/FitnessActivityClient';
import { ZonedDateTime } from '@ms-rewards/date-lib';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Api } from '../../../../../core/api';
import {
    positive,
    required,
    useModel,
    wrapInFormField
} from '../../../../../core/forms';
import { injectTSDI } from '../../../../../core/tsdi';
import { UserContext } from '../../../../../users/user';
import { EmployeeContext } from '../../../index';

export function useStore(onSuccess?: () => void) {
    const { fitnessActivityClient } = injectTSDI(Api);
    const employee = React.useContext(EmployeeContext);
    const user = React.useContext(UserContext);

    const addStepsRequest = useWrapRequest(
        async (data: DailyStepsEvent) => {
            const response =
                await fitnessActivityClient.admin.activitySupportControllerCreateStepsActivity(
                    data
                );
            if (onSuccess) {
                onSuccess();
            }
            return response;
        },
        { successMessage: () => 'Steps added' }
    );

    const model = useModel(() => {
        const form = new FormState({
            stepCount: new FieldState<number>(0).validators(
                required(),
                positive()
            ),
            userDay: new FieldState<ZonedDateTime>(
                ZonedDateTime.now('Europe/Berlin').startOf('day')
            ).validators(required()),
            timeZone: new FieldState<string>('Europe/Berlin').validators(
                required()
            )
        });
        return {
            form,
            fields: {
                stepCount: wrapInFormField(form.$.stepCount),
                userDay: wrapInFormField(form.$.userDay),
                timeZone: wrapInFormField(form.$.timeZone)
            }
        };
    });

    const onSubmitHandler = async () => {
        const validation = await model.form.validate();
        const { stepCount, userDay } = model.fields;

        if (validation.hasError) {
            throw new Error(model.form.error as string);
        }
        try {
            const timestamp = userDay.value.startOf('day');
            const userDayString = timestamp.format('yyyy-MM-DD');
            await addStepsRequest.request([
                {
                    messageId: uuidv4().toString(),
                    userId: employee.mySportsId || user.id,
                    timestamp: timestamp.toString(),
                    userDay: userDayString,
                    type: ActivityEventType.STEPS_PER_DAY,
                    stepCount: stepCount.value,
                    dateTimeRange: {
                        from: `${timestamp.toString()}`,
                        to: `${timestamp.addDays(1).toString()}`
                    },
                    priority: ActivityPriority.FORCE_OVERRIDE,
                    sources: ['com.mysports.support.admin-portal'],
                    fitnessApp: SupportedFitnessApp.MOVE_REPUBLIC_ADMIN
                }
            ]);
        } catch (err) {
            alert(err);
        }
    };

    return {
        fields: model.fields,
        errors: model.form.error,
        onSubmitHandler,
        addStepsRequest: addStepsRequest
    };
}
